import axios from "axios";
import { getToken } from "../Utils/Common";
import token_valid from "./TokenValid";

// ---------------------------------------------------------------------------
// Loads the actual records a report is generated from, per report source.
// Used for (a) the WYSIWYG preview record, (b) the single-record picker, and
// (c) bulk date-range generation. Self-contained (own auth helpers) so it does
// not depend on each domain service's differing error/return conventions.
// ---------------------------------------------------------------------------

const BASE = process.env.REACT_APP_API_URL;

async function authGet(url) {
  const valid = await token_valid();
  if (!valid) return null;
  const token = getToken();
  try {
    const r = await axios.get(BASE + url, { headers: { Authorization: "Bearer " + token } });
    return r.data;
  } catch (e) {
    console.log("[ReportRecordLoader] GET " + url + " failed", e);
    return null;
  }
}

async function authPost(url, body) {
  const valid = await token_valid();
  if (!valid) return null;
  const token = getToken();
  try {
    const r = await axios.post(BASE + url, body, {
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
    });
    return r.data;
  } catch (e) {
    console.log("[ReportRecordLoader] POST " + url + " failed", e);
    return null;
  }
}

const LIMIT = 500;

// Per-source: how to fetch one full record, list rows, and label/date them.
export const SOURCE_CONFIG = {
  SUB_ORDER: {
    getOne: (id) => authGet("/api/sub-order/" + id),
    // Slim header-only projection (no line items / payments) — fast to load.
    list: () => authGet("/api/sub-order/summary/" + LIMIT),
    idField: "orderId",
    dateField: "orderDate",
    label: (r) => `#${r.orderId} · ${(r.subOrderPerson && r.subOrderPerson.personName) || "—"}`,
  },
  IMPORT_ITEM: {
    getOne: (id) => authGet("/api/import/" + id),
    list: () => authGet("/api/import/all"),
    idField: "importId",
    dateField: "addedDate",
    label: (r) => `${r.product_code || ""} · ${r.itemName || ""}`,
  },
  IMPORT_BILL: {
    getOne: (id) => authGet("/api/import/bill/find/" + id),
    list: () => authGet("/api/import/bill/all"),
    idField: "bill_id",
    dateField: "createdDate",
    label: (r) => `${r.billNo || r.bill_id} · ${r.shop || ""}`,
  },
  EXPORT_PRODUCT: {
    getOne: (id) => authGet("/api/export/product/" + id),
    list: () => authPost("/api/export/product/all/0/" + LIMIT, {}),
    idField: "product_id",
    dateField: null,
    label: (r) => `${r.barcode || ""} · ${r.name || ""}`,
  },
  EXPORT_PRODUCTION: {
    getOne: (id) => authGet("/api/export/production/" + id),
    list: () => authPost("/api/export/production/all/0/" + LIMIT, {}),
    idField: "productionId",
    dateField: "productionDate",
    label: (r) => `${r.billNumber || r.productionId} · ${r.productName || ""}`,
  },
};

export const getSourceConfig = (source) => SOURCE_CONFIG[source] || null;

/**
 * Flattened SubOrder line items (each merged with its order header fields) for an
 * optional date range — one query, no full-order hydration. Used to preview /
 * export a Line-Items-grain report across many orders.
 */
export async function loadSubOrderLineItems(from, to, limit = 5000) {
  const params = [];
  if (from) params.push("from=" + encodeURIComponent(from));
  if (to) params.push("to=" + encodeURIComponent(to));
  const qs = params.length ? "?" + params.join("&") : "";
  const data = await authGet("/api/sub-order/line-items/" + limit + qs);
  return Array.isArray(data) ? data : [];
}

// A list endpoint may return a Spring Page ({content:[...]}) or a raw array.
function asArray(res) {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.content)) return res.content;
  return [];
}

const isRecord = (v) => v != null && typeof v === "object";

/** Fetch one full record (with nested collections) by id. */
export async function loadRecord(source, id) {
  const cfg = getSourceConfig(source);
  if (!cfg) return null;
  const data = await cfg.getOne(id);
  return isRecord(data) ? data : null;
}

/** List rows for the record picker. Returns `{ id, label, date, raw }[]`. */
export async function loadRecordOptions(source) {
  const cfg = getSourceConfig(source);
  if (!cfg) return [];
  const rows = asArray(await cfg.list());
  return rows.map((r) => ({
    id: r[cfg.idField],
    label: cfg.label(r),
    date: cfg.dateField ? r[cfg.dateField] : null,
    raw: r,
  }));
}

const datePart = (v) => (v ? String(v).split(/[ T]/)[0] : "");

function withinRange(value, startStr, endStr) {
  const d = datePart(value);
  if (!d) return false;
  if (startStr && d < startStr) return false;
  if (endStr && d > endStr) return false;
  return true;
}

/**
 * Full records for bulk generation. When the source has a date field, filters by
 * [startStr, endStr] (yyyy-mm-dd). Fetches each match in full so nested tables
 * render. `onProgress(done, total)` reports progress.
 */
export async function loadRecordsInRange(source, startStr, endStr, onProgress) {
  const cfg = getSourceConfig(source);
  if (!cfg) return [];
  let rows = asArray(await cfg.list());
  if (cfg.dateField) {
    rows = rows.filter((r) => withinRange(r[cfg.dateField], startStr, endStr));
  }
  const out = [];
  for (let i = 0; i < rows.length; i += 1) {
    const id = rows[i][cfg.idField];
    const full = id != null ? await loadRecord(source, id) : rows[i];
    if (isRecord(full)) out.push(full);
    if (onProgress) onProgress(i + 1, rows.length);
  }
  return out;
}
